import { useState } from "react";
import { Sparkles, RefreshCw, Loader2, Pencil } from "lucide-react";
import { toast } from "sonner";
import api, { errorText, duckTitle } from "../lib/api";
import { StoryEditor } from "./StoryEditor";

const Chip = ({ testId, onClick, disabled, children }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    disabled={disabled}
    className="inline-flex items-center gap-1.5 rounded-full border border-border px-3 py-1 text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
  >
    {children}
  </button>
);

export const DuckStoryCard = ({ creation, onUpdate }) => {
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const story = creation.story;

  const regenerate = async () => {
    if (story?.edited_by_user && !window.confirm("You edited this story by hand. Replace it with a new AI-written one?")) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/creations/${creation.id}/story`);
      onUpdate?.(data);
      toast.success("Duckify wrote a new duck story");
    } catch (e) {
      toast.error(errorText(e, "Could not write the story"));
    } finally {
      setBusy(false);
    }
  };

  if (!story && !onUpdate) return null;
  return (
    <div data-testid="duck-story" className="rounded-3xl border border-border bg-card/60 p-5 space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="flex items-center gap-2 text-xs font-mono2 uppercase tracking-widest text-muted-foreground">
          <Sparkles className="h-4 w-4" /> Duck story
          {story?.edited_by_user && <span data-testid="story-edited-chip" className="rounded-full bg-muted px-2 py-0.5 text-[10px] normal-case tracking-normal">edited by you</span>}
        </p>
        {onUpdate && !editing && (
          <div className="flex gap-1.5">
            <Chip testId="story-edit-btn" onClick={() => setEditing(true)} disabled={busy}><Pencil className="h-3.5 w-3.5" /> {story ? "Edit" : "Write my own"}</Chip>
            <Chip testId="story-regenerate-btn" onClick={regenerate} disabled={busy}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} {story ? "Rewrite" : "Write it"}
            </Chip>
          </div>
        )}
      </div>
      {editing ? (
        <StoryEditor creation={creation} onCancel={() => setEditing(false)} onSaved={(data) => { onUpdate?.(data); setEditing(false); }} />
      ) : story ? (
        <>
          <h3 data-testid="story-name" className="font-heading text-xl sm:text-2xl font-bold tracking-tight">{duckTitle(creation)}</h3>
          <p data-testid="story-description" className="text-sm text-muted-foreground whitespace-pre-line">{story.description}</p>
          {story.stale_name && (
            <p data-testid="story-stale-name" className="rounded-xl bg-muted px-3 py-2 text-xs text-muted-foreground">
              Your story still says “{story.stale_name}”.{onUpdate && " Edit it any time to use the new name."}
            </p>
          )}
        </>
      ) : (
        <p className="text-sm text-muted-foreground">No story yet — let Duckify name this duck and write print tips, or write your own.</p>
      )}
    </div>
  );
};
